import React, { Fragment, useEffect, useState } from "react";
import { Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import _ from "lodash";
import {
   FilterDateTime, WarehouseShippingScheduleCurrentWeekGrid, WarehouseShippingScheduleLateShipGrid,
   WarehouseShippingScheduleConsignedGrid, WarehouseShippingScheduleSampleOrderGrid,
} from "./";
import { api, constants, helpers, dateHelpers, filterHelpers } from "../utils";
import { DesktopContainer } from "../layouts";
import { toast } from "react-toastify";
import Select from "react-select";
import ServiceThreadSpinner from "./ServiceThreadSpinner";

export default function ShippingSchedule() {
   const [selectedSalesPerson, setSelectedSalesPerson] = useState({
      value: "",
      label: "--- All ---",
   });
   const [selectedShippingCompany, setSelectedShippingCompany] = useState({
      value: "",
      label: "--- All ---",
   });
   const [selectedSite, setSelectedSite] = useState({
      value: "",
      label: "--- All ---",
   });
   const [filters, setFilters] = useState([
      {
         filterName: "ShipMethod",
         value: "",
         label: "--- All ---",
         filteredDisplay: "undefined: --- All ---",
      },
      {
         filterName: "salesPersonId",
         value: "",
         label: "--- All ---",
         filteredDisplay: "undefined: --- All ---",
      },
   ]);

   const [barcodeImageUrl, setBarcodeImageUrl] = useState("");
   const [barcodeLoading, setBarcodeLoading] = useState(false);
   const [barcodeString, setBarcodeString] = useState("");
   const [bomSites, setBomSites] = useState([]);
   const [data, setData] = useState([]);
   const [downloading, setDownloading] = useState(false);
   const [dropShippingScheduleSortDirection, setDropShippingScheduleSortDirection] = useState("asc");
   const [dropShippingScheduleSortField, setDropShippingScheduleSortField] = useState("CustomerId");
   const [loaded, setLoaded] = useState(true);
   const [machinePartScheduleSortDirection, setMachinePartScheduleSortDirection] = useState("asc");
   const [machinePartScheduleSortField, setMachinePartScheduleSortField] = useState("CustomerId");
   const [periodType, setPeriodType] = useState("todayRadio");
   const [salesPersons, setSalesPersons] = useState([]);
   const [sampleOrderSortDirection, setSampleScheduleSortDirection] = useState("asc");
   const [sampleOrderSortField, setSampleScheduleSortField] = useState("CustomerId");
   const [shippingCompanies, setShippingCompanies] = useState([]);
   const [shippingNotes, setShippingNotes] = useState("");
   const [shipScheduleSortDirection, setShipScheduleSortDirection] = useState("asc");
   const [shipScheduleSortField, setShipScheduleSortField] = useState("CustomerId");
   const [showModal, setShowModal] = useState(false);
   const [specialInstructions, setSpecialInstructions] = useState("");
   const [tomorrowDate, setTomorrowDate] = useState("");
   const [weekEndDate, setWeekEndDate] = useState("");
   const [weekStartDate, setWeekStartDate] = useState("");
   const [working, setWorking] = useState(false);
   const currentDate = dateHelpers.toLongDateString(new Date());

   useEffect(() => {
      document.title = `Shipping Schedule`
      getSalesPersons();
      getCompanies();
      getBomSites();
      getTomorrow();
      getWeekStart();
      getWeekEnd();
   }, []);

   useEffect(() => {
      getShippingSchedule();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      if (loaded || working) {
         return;
      }
      getShippingSchedule();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loaded, periodType]);

   // The "Scan Order" modal's barcode image. Fetched as a blob (not an <img src>) so the Bearer token reaches the
   // sign-in-required Sales/ShipScheduleOrderBarcode action. The object URL is revoked whenever the order changes
   // or the modal closes, so a stale blob is never left referenced.
   useEffect(() => {
      if (!showModal || !barcodeString) {
         setBarcodeImageUrl("");
         return;
      }

      let objectUrl = "";
      let cancelled = false;
      setBarcodeLoading(true);
      api.getBlob(`Sales/ShipScheduleOrderBarcode/${encodeURIComponent(barcodeString)}`, "Unable to load the order barcode.")
         .then((response) => {
            if (cancelled) return;
            if (response?.data === false) {
               // Clear any previous order's image so the modal never shows a barcode for the wrong order.
               setBarcodeImageUrl("");
               toast.error(response.message);
               return;
            }
            objectUrl = URL.createObjectURL(response.data);
            setBarcodeImageUrl(objectUrl);
         })
         .finally(() => {
            if (!cancelled) setBarcodeLoading(false);
         });

      return () => {
         cancelled = true;
         if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
   }, [showModal, barcodeString]);

   function createPayload() {
      const payload = {};
      payload.BomSite = selectedSite.value;
      payload.ShipMethod = selectedShippingCompany.value;
      payload.salesPersonId = selectedSalesPerson.value;
      if (periodType === "todayRadio") {
         payload.endDate = dateHelpers.utcDayEnd(currentDate);
      } else if (periodType === "lateRadio") {
         payload.endDate = dateHelpers.utcDayEnd(weekEndDate);
      } else if (periodType === "tomorrowRadio") {
         payload.startDate = dateHelpers.utcDayStart(tomorrowDate);
         payload.endDate = dateHelpers.utcDayEnd(tomorrowDate);
      } else if (periodType === "thisWeekRadio") {
         payload.startDate = dateHelpers.utcDayStart(weekStartDate);
         payload.endDate = dateHelpers.utcDayEnd(weekEndDate);
      } else if (periodType === "byDateRangeRadio") {
         payload.startDate = dateHelpers.utcDayStart(filterHelpers.getValue(filters, "startDate"));
         payload.endDate = dateHelpers.utcDayEnd(filterHelpers.getValue(filters, "endDate"));
      }

      payload.ShipScheduleSortField = shipScheduleSortField;
      payload.ShipScheduleSortDirection = shipScheduleSortDirection;
      payload.MachinePartScheduleSortField = machinePartScheduleSortField;
      payload.MachinePartScheduleSortDirection = machinePartScheduleSortDirection;
      payload.DropShippingScheduleSortField = dropShippingScheduleSortField;
      payload.DropShippingScheduleSortDirection = dropShippingScheduleSortDirection;
      payload.InShippingSchedule = true;
      payload.IsWarehouse = true;
      payload.PeriodType = periodType;
      return payload;
   }

   function dropShippingScheduleSortChange(field, direction) {
      if (field !== dropShippingScheduleSortField) {
         setDropShippingScheduleSortField(field);
         setDropShippingScheduleSortDirection("asc");
      } else {
         setDropShippingScheduleSortDirection(direction);
      }
      setLoaded(false);
   }

   function exportConsignedOrdersXlsx() {
      api
         .postGetBlob("Sales/ExportShipScheduleConsignedOrderXLSX", {
            SalesPersonId: selectedSalesPerson.value,
         })
         .then((r) => {
            if (r.data.success === false) {
               if (_.isString(r?.data?.message)) {
                  toast.error(r?.data?.message);
               }
               return;
            }
            if (r.data) {
               const XLSX_FILENAME = selectedSalesPerson.value
                  ? `${selectedSalesPerson.value}_ConsignedShipSchedule.xlsx`
                  : "ConsignedShipSchedule.xlsx";
               helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx);
            } else {
               toast.error(r.message || "Error downloading report");
            }
         })
         .catch(api.catchHandler);
   }

   function exportSampleOrderXlsx() {
      api
         .postGetBlob("Sales/ExportShipScheduleSampleOrderXLSX", {
            SalesPersonId: selectedSalesPerson.value,
         })
         .then((r) => {
            if (r.data.success === false) {
               if (_.isString(r?.data?.message)) {
                  toast.error(r?.data?.message);
               }
               return;
            }
            if (r.data) {
               const XLSX_FILENAME = selectedSalesPerson.value
                  ? `${selectedSalesPerson.value}_SamplesShipSchedule.xlsx`
                  : "SamplesShipSchedule.xlsx"; // `${selectedSalesPerson.value}_ShipScheduleSampleOrders.xlsx`
               helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx);
            } else {
               toast.error(r.message || "Error downloading report");
            }
         })
         .catch(api.catchHandler);
   }

   function exportXlsx(schedule) {
      let payload = createPayload();
      payload.Schedule = schedule;
      api
         .postGetBlob("Sales/ExportShipScheduleCurrentWeekXLSX", payload)
         .then((r) => {
            if (r.data.success === false) {
               if (_.isString(r?.data?.message)) {
                  toast.error(r?.data?.message);
               }
               return;
            }
            if (r.data) {
               let XLSX_FILENAME = selectedSalesPerson.value ? `${selectedSalesPerson.value}_` : "";
               if (schedule === constants.SHIPPING_SCHEDULE_TYPES.DropShipping)
                  XLSX_FILENAME = `${XLSX_FILENAME}DropShipSchedule.xlsx`
               else
                  XLSX_FILENAME = `${XLSX_FILENAME}${schedule === constants.SHIPPING_SCHEDULE_TYPES.MachinePartShipping
                     ? 'MachinePartSchedule'
                     : 'ShipSchedule'}.xlsx`
               helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx)
            } else {
               toast.error(r.message || "Error downloading report");
            }
         })
         .catch(api.catchHandler);
   }

   function exportLateXlsx(schedule) {
      let payload = createPayload();
      payload.Schedule = schedule;
      api
         .postGetBlob("Sales/ExportLateShipScheduleXLSX", payload)
         .then((r) => {
            if (r.data.success === false) {
               if (_.isString(r?.data?.message)) {
                  toast.error(r?.data?.message);
               }
               return;
            }
            if (r.data) {
               let XLSX_FILENAME = selectedSalesPerson.value ? `${selectedSalesPerson.value}_` : "";
               if (schedule === constants.SHIPPING_SCHEDULE_TYPES.DropShipping)
                  XLSX_FILENAME = `${XLSX_FILENAME}LateDropShipSchedule.xlsx`
               else
                  XLSX_FILENAME = `${XLSX_FILENAME}${schedule === constants.SHIPPING_SCHEDULE_TYPES.MachinePartShipping
                     ? 'LateMachinePartSchedule'
                     : 'LateShipSchedule'}.xlsx`
               helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx)
            } else {
               toast.error(r.message || "Error downloading report");
            }
         })
         .catch(api.catchHandler);
   }

   function getCompanies() {
      api
         .fetch("Reference/GetShippingCompanyList")
         .then((response) => {
            if (response && response.data.success) {
               let list = _.map(response.data.companies, function (x) {
                  return { value: x.ddlValue, label: x.ddlDescription };
               });
               list.unshift({ value: "", label: "--- All ---" });
               setShippingCompanies(list);
            } else {
               toast.error("Failed to get companies");
            }
         })
         .catch(api.catchHandler);
   }

   function getSalesPersons() {
      api
         .fetch("Reference/GetSalespeopleList")
         .then((response) => {
            if (response && response.data.success) {
               let list = _.map(response.data.salespeople, function (x) {
                  return { value: x.salespersonId, label: x.salespersonText };
               });
               list.unshift({ value: "", label: "--- All ---" });
               setSalesPersons(list);
            } else {
               toast.error("Failed to get salespersons.");
            }
         })
         .catch(api.catchHandler);
   }

   function getBomSites() {
      api
         .fetch("Reference/LoadWarehouseShippingSites")
         .then((response) => {
            if (response && response.data.success) {
               let list = _.map(response.data.sites, function (x) {
                  return { value: x.value, label: x.label };
               });
               list.unshift({ value: "", label: "--- All ---" });
               setBomSites(list);
            } else {
               toast.error("Failed to get BOM header sites.");
            }
         })
         .catch(api.catchHandler);
   }

   function getShippingSchedule() {
      setWorking(true);
      setLoaded(false);
      let payload = createPayload();
      api
         .post("Sales/LoadSalesDashboardShipSchedule", payload)
         .then((response) => {
            if (response && response.data) {
               let newData = response.data.shipScheduleData;

               //--------------------------------------------------------------------------------------------
               // CP 2024-11-07 (STOP overriding the order type!) Some features like adding tracking numbers
               // need the correct order type. This was mapped as a 1 just for printing BOL's I believe.
               // So instead I have hard-coded the individual schedule grids to use a 1 (SOP) or 2 (Consigned)
               // as the order types in the sales order hover menu options for BOL's to work properly.
               //--------------------------------------------------------------------------------------------
               //newData.shipScheduleData = _.map(newData.shipScheduleData, sd => ({ ...sd, orderType: 1 }));
               setData(newData);
            }
         })
         .catch(api.catchHandler)
         .finally(() => handleLoading());
   }

   function getTomorrow() {
      let today = new Date();
      let tomorrow = new Date();
      setTomorrowDate(dateHelpers.toLongDateString(tomorrow.setDate(today.getDate() + 1)));
   }

   function getWeekEnd() {
      let today = new Date();
      let weekday = today.getDay();

      let date = new Date();
      let monthDay = date.getDate();

      switch (weekday) {
         case 0:
            setWeekEndDate(dateHelpers.toLongDateString(date.setDate(monthDay + 5)));
            break;
         case 1:
            setWeekEndDate(dateHelpers.toLongDateString(date.setDate(monthDay + 4)));
            break;
         case 2:
            setWeekEndDate(dateHelpers.toLongDateString(date.setDate(monthDay + 3)));
            break;
         case 3:
            setWeekEndDate(dateHelpers.toLongDateString(date.setDate(monthDay + 2)));
            break;
         case 4:
            setWeekEndDate(dateHelpers.toLongDateString(date.setDate(monthDay + 1)));
            break;
         case 5:
            setWeekEndDate(dateHelpers.toLongDateString(date));
            break;
         case 6:
            setWeekEndDate(dateHelpers.toLongDateString(date.setDate(monthDay + 6)));
            break;
         default:
            break;
      }
   }

   function getWeekStart() {
      let today = new Date();
      let weekday = today.getDay();

      let date = new Date();
      let monthDay = date.getDate();

      switch (weekday) {
         case 0:
            setWeekStartDate(dateHelpers.toLongDateString(date.setDate(monthDay + 1)));
            break;
         case 1:
            setWeekStartDate(dateHelpers.toLongDateString(date));
            break;
         case 2:
            setWeekStartDate(dateHelpers.toLongDateString(date.setDate(monthDay - 1)));
            break;
         case 3:
            setWeekStartDate(dateHelpers.toLongDateString(date.setDate(monthDay - 2)));
            break;
         case 4:
            setWeekStartDate(dateHelpers.toLongDateString(date.setDate(monthDay - 3)));
            break;
         case 5:
            setWeekStartDate(dateHelpers.toLongDateString(date.setDate(monthDay - 4)));
            break;
         case 6:
            setWeekStartDate(dateHelpers.toLongDateString(date.setDate(monthDay + 2)));
            break;
         default:
            break;
      }
   }

   function handleChangePeriodType(e) {
      setLoaded(false);
      setPeriodType(e);
   }

   function handleLoading() {
      setLoaded(true);
      setWorking(false);
   }

   function handleScanOrder(order) {
      setShowModal(true);
      setBarcodeString(order.orderId);
      setSpecialInstructions(order.addressNote);
      setShippingNotes(order.commentText);
   }

   function onFilterChange(changedFilter) {
      const update = filterHelpers.getFilters(filters, changedFilter);
      setFilters(update);
   }

   function onMachinePartScheduleSortChange(field, direction) {
      if (field !== machinePartScheduleSortField) {
         setMachinePartScheduleSortField(field);
         setMachinePartScheduleSortDirection("asc");
      } else {
         setMachinePartScheduleSortDirection(direction);
      }
      setLoaded(false);
   }

   function onSampleOrderScheduleSortChange(field, direction) {
      if (field !== machinePartScheduleSortField) {
         setSampleScheduleSortField(field);
         setSampleScheduleSortDirection("asc");
      } else {
         setSampleScheduleSortDirection(direction);
      }
      setLoaded(false);
   }

   function onShipScheduleSortChange(field, direction) {
      if (field !== shipScheduleSortField) {
         setShipScheduleSortField(field);
         setShipScheduleSortDirection("asc");
      } else {
         setShipScheduleSortDirection(direction);
      }
      setLoaded(false);
   }

   function printShippingSchedule() {
      let payload = {};
      payload.ShipMethod = selectedShippingCompany.value;
      payload.salesPersonId = selectedSalesPerson.value;

      if (periodType === "todayRadio") {
         payload.startDate = dateHelpers.utcDayEnd(currentDate);
         payload.endDate = dateHelpers.utcDayEnd(currentDate);
      } else if (periodType === "tomorrowRadio") {
         payload.startDate = dateHelpers.utcDayStart(tomorrowDate);
         payload.endDate = dateHelpers.utcDayEnd(tomorrowDate);
      } else if (periodType === "thisWeekRadio") {
         payload.startDate = dateHelpers.utcDayStart(weekStartDate);
         payload.endDate = dateHelpers.utcDayEnd(weekEndDate);
      } else if (periodType === "byDateRangeRadio") {
         payload.startDate = dateHelpers.utcDayStart(filterHelpers.getValue(filters, "startDate"));
         payload.endDate = dateHelpers.utcDayEnd(filterHelpers.getValue(filters, "endDate"));
      }

      payload.ShipScheduleSortField = shipScheduleSortField;
      payload.ShipScheduleSortDirection = shipScheduleSortDirection;
      payload.MachinePartScheduleSortField = machinePartScheduleSortField;
      payload.MachinePartScheduleSortDirection = machinePartScheduleSortDirection;
      payload.DropShippingScheduleSortField = dropShippingScheduleSortField;
      payload.DropShippingScheduleSortDirection = dropShippingScheduleSortDirection;
      payload.InShippingSchedule = true;
      payload.PeriodType = periodType;

      setDownloading(true);
      api
         .postDownloadBinary(
            "Report/PrintShippingScheduleReport",
            payload,
            constants.MIME_TYPES.pdf,
            `${dateHelpers.monthDayAndYear(Date.now())}`,
            "There was an error downloading the Shipping Schedule Report."
         ) // add todays date as last param
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setDownloading(false));
   }

   return (
      <DesktopContainer screenName="Open Sales Orders">
         <Row className="mt-3 d-flex justify-content-center align-items-center filter-header">
            <Col xs="8">
               <Row className="mt-3 d-flex align-items-center">
                  <Col xs="3" className="d-flex justify-content-end">
                     <p className="m-0" style={{ color: "white", fontSize: "1.25rem" }}>
                        Freight Carrier:
                     </p>
                  </Col>
                  <Col xs="8">
                     <Select
                        classNamePrefix="serviceThreadSelect"
                        options={shippingCompanies}
                        value={selectedShippingCompany}
                        onChange={(e) => setSelectedShippingCompany(e)}
                     />
                  </Col>
               </Row>

               {/* Period Radio Buttons */}
               <Row className="mt-3 d-flex align-items-center">
                  <Col xs="3" className="d-flex justify-content-end">
                     <p className="m-0" style={{ color: "white", fontSize: "1.25rem" }}>
                        Period:
                     </p>
                  </Col>
                  <Col xs="8">
                     <Label style={{ color: "white", fontSize: "1.25rem" }}>
                        <Input
                           type="radio"
                           name="today-radio"
                           value="todayRadio"
                           className="mx-1"
                           checked={periodType === "todayRadio"}
                           disabled={working}
                           onChange={(e) => handleChangePeriodType(e.target.value)}
                        />
                        {" "}Today
                     </Label>
                     {"   "}
                     <Label style={{ color: "white", fontSize: "1.25rem" }}>
                        <Input
                           type="radio"
                           name="tomorrow-radio"
                           value="tomorrowRadio"
                           className="mx-1"
                           checked={periodType === "tomorrowRadio"}
                           disabled={working}
                           onChange={(e) => handleChangePeriodType(e.target.value)}
                        />
                        {" "}Tomorrow
                     </Label>
                     {"   "}
                     <Label style={{ color: "white", fontSize: "1.25rem" }}>
                        <Input
                           type="radio"
                           name="this-week-radio"
                           value="thisWeekRadio"
                           className="mx-1"
                           checked={periodType === "thisWeekRadio"}
                           disabled={working}
                           onChange={(e) => handleChangePeriodType(e.target.value)}
                        />
                        {" "}This Week
                     </Label>
                     {"   "}
                     <Label style={{ color: "white", fontSize: "1.25rem" }}>
                        <Input
                           type="radio"
                           name="by-date-range-radio"
                           value="byDateRangeRadio"
                           className="mx-1"
                           checked={periodType === "byDateRangeRadio"}
                           disabled={working}
                           onChange={(e) => handleChangePeriodType(e.target.value)}
                        />
                        {" "}By Date Range
                     </Label>
                     {"   "}
                     <Label style={{ color: "white", fontSize: "1.25rem" }}>
                        <Input
                           type="radio"
                           name="late-radio"
                           value="lateRadio"
                           className="mx-1"
                           checked={periodType === "lateRadio"}
                           disabled={working}
                           onChange={(e) => handleChangePeriodType(e.target.value)}
                        />
                        {" "}Late Shipments
                     </Label>
                  </Col>
               </Row>
               {periodType === "todayRadio" ? (
                  <Row className="mt-3 d-flex align-items-center">
                     <Col xs="3" className="d-flex justify-content-end">
                        <p className="m-0" style={{ color: "white", fontSize: "1.25rem" }}>
                           End Date:
                        </p>
                     </Col>
                     <Col xs="8">
                        <Input type="text" disabled value={currentDate} />
                     </Col>
                  </Row>
               ) : null}
               {periodType === "tomorrowRadio" ? (
                  <>
                     <Row className="mt-3 d-flex align-items-center">
                        <Col xs="3" className="d-flex justify-content-end">
                           <p className="m-0" style={{ color: "white", fontSize: "1.25rem" }}>
                              Start Date:
                           </p>
                        </Col>
                        <Col xs="8">
                           <Input type="text" disabled value={tomorrowDate} />
                        </Col>
                     </Row>
                     <Row className="mt-3 d-flex align-items-center">
                        <Col xs="3" className="d-flex justify-content-end">
                           <p className="m-0" style={{ color: "white", fontSize: "1.25rem" }}>
                              End Date:
                           </p>
                        </Col>
                        <Col xs="8">
                           <Input type="text" disabled value={tomorrowDate} />
                        </Col>
                     </Row>
                  </>
               ) : null}
               {periodType === "thisWeekRadio" ? (
                  <>
                     <Row className="mt-3 d-flex align-items-center">
                        <Col xs="3" className="d-flex justify-content-end">
                           <p className="m-0" style={{ color: "white", fontSize: "1.25rem" }}>
                              Start Date:
                           </p>
                        </Col>
                        <Col xs="8">
                           <Input type="text" disabled value={weekStartDate} />
                        </Col>
                     </Row>
                     <Row className="mt-3 d-flex align-items-center">
                        <Col xs="3" className="d-flex justify-content-end">
                           <p className="m-0" style={{ color: "white", fontSize: "1.25rem" }}>
                              End Date:
                           </p>
                        </Col>
                        <Col xs="8">
                           <Input type="text" disabled value={weekEndDate} />
                        </Col>
                     </Row>
                  </>
               ) : null}
               {periodType === "byDateRangeRadio" ? (
                  <>
                     <Row className="mt-3 d-flex align-items-center">
                        <Col xs="3" className="d-flex justify-content-end">
                           <p className="m-0" style={{ color: "white", fontSize: "1.25rem" }}>
                              Start Date:
                           </p>
                        </Col>
                        <Col xs="8">
                           <FilterDateTime
                              filterName="startDate"
                              value={filterHelpers.getValue(filters, "startDate")}
                              onChangeCallback={onFilterChange}
                           />
                        </Col>
                     </Row>
                     <Row className="mt-3 d-flex align-items-center">
                        <Col xs="3" className="d-flex justify-content-end">
                           <p className="m-0" style={{ color: "white", fontSize: "1.25rem" }}>
                              End Date:
                           </p>
                        </Col>
                        <Col xs="8">
                           <FilterDateTime
                              filterName="endDate"
                              value={filterHelpers.getValue(filters, "endDate")}
                              onChangeCallback={onFilterChange}
                           />
                        </Col>
                     </Row>
                  </>
               ) : null}
               {/* Sales Person Filter */}
               <Row className="mt-3 d-flex align-items-center">
                  <Col xs="3" className="d-flex justify-content-end">
                     <p className="m-0" style={{ color: "white", fontSize: "1.25rem" }}>
                        Sales Person:
                     </p>
                  </Col>
                  <Col xs="8">
                     <Select
                        classNamePrefix="serviceThreadSelect"
                        options={salesPersons}
                        value={selectedSalesPerson}
                        onChange={(e) => setSelectedSalesPerson(e)}
                     />
                  </Col>
               </Row>
               {/*BOM Site Filter */}
               <Row className="mt-3 d-flex align-items-center">
                  <Col xs="3" className="d-flex justify-content-end">
                     <p className="m-0" style={{ color: "white", fontSize: "1.25rem" }}>
                        BOM Site:
                     </p>
                  </Col>
                  <Col xs="8">
                     <Select
                        classNamePrefix="serviceThreadSelect"
                        options={bomSites}
                        value={selectedSite}
                        onChange={(e) => setSelectedSite(e)}
                     />
                  </Col>
               </Row>
               {/* SHOW or PRINT Buttons */}
               <Row className="mt-3 d-flex">
                  <Col xs="3"></Col>
                  <Col xs="8">
                     <Button className="shredButton" onClick={() => getShippingSchedule()}>
                        SHOW SHIPPING SCHEDULE
                     </Button>
                     <Button className="shredButton mx-2" onClick={() => printShippingSchedule()} disabled={downloading}>
                        PRINT SHIPPING SCHEDULE
                     </Button>
                  </Col>
               </Row>
            </Col>
         </Row>
         <Row>
            <ServiceThreadSpinner on={!loaded}>
               {/* Late Shipment Grids will only show on the today option or the late shipment option 
                  The lists containing the data for these will not be populated for anything else.
                  Late standard orders should show above all other grids that get populated. */}
               {data && data.lateScheduleData?.length > 0 ? (
                  <Row className="mt-3 d-flex justify-content-center">
                     <Row>
                        <Col xs="8">
                           <h5 style={{ textDecoration: "underline" }} >
                              Late Shipment Orders
                           </h5>
                        </Col>
                     </Row>
                     <Row>
                        <Col>
                           <WarehouseShippingScheduleLateShipGrid
                              data={data.lateScheduleData}
                              onUpdateRow={() => setLoaded(false)}
                              exportXlsxCallback={() => exportLateXlsx(constants.SHIPPING_SCHEDULE_TYPES.AllShipping)}
                              schedule={constants.SHIPPING_SCHEDULE_TYPES.AllShipping}
                              scanCallback={handleScanOrder}
                           />
                        </Col>
                     </Row>
                  </Row>
               ) : null}

               {/* These grids do NOT show if the late shipment option is chosen */}
               {periodType !== "lateRadio" ? (
                  <>
                     {/* NO SCHEDULED SHIPMENTS FOUND */}
                     {data?.shipScheduleData?.length <= 0 &&
                        data?.dropShippingData?.length <= 0 &&
                        data?.machinePartScheduleData?.length <= 0 &&
                        data?.consignedOrdersData?.length <= 0 &&
                        data?.sampleOrderData?.length <= 0 ? (
                        <Row className="mt-3 d-flex justify-content-center">
                           <Row>
                              <Col xs="8">
                                 <h5 style={{ color: 'orangered' }} >
                                    There are no scheduled shipments for the selected criteria
                                 </h5>
                              </Col>
                           </Row>
                        </Row>
                     ) : null}

                     {/* REGULAR ITEM SHIP SCHEDULE */}
                     {data && data.shipScheduleData?.length > 0 ? (
                        <Row className="mt-3 d-flex justify-content-center">
                           <Row>
                              <Col xs="8">
                                 <h5 style={{ textDecoration: "underline" }} >
                                    Standard Orders
                                 </h5>
                              </Col>
                           </Row>
                           <Row>
                              <Col>
                                 <WarehouseShippingScheduleCurrentWeekGrid
                                    data={data.shipScheduleData}
                                    sortCallback={onShipScheduleSortChange}
                                    sortField={shipScheduleSortField}
                                    sortDirection={shipScheduleSortDirection}
                                    onUpdateRow={() => setLoaded(false)}
                                    exportXlsxCallback={() => exportXlsx(constants.SHIPPING_SCHEDULE_TYPES.AllShipping)}
                                    schedule={constants.SHIPPING_SCHEDULE_TYPES.AllShipping}
                                    scanCallback={handleScanOrder}
                                 />
                              </Col>
                           </Row>
                        </Row>
                     ) : null}

                     {/* DROP SHIP ORDER SHIP SCHEDULE */}
                     {data && data.dropShippingData?.length > 0 ? (
                        <Row className="mt-3 d-flex justify-content-center">
                           <Row>
                              <Col xs="8">
                                 <h5 style={{ textDecoration: "underline" }} >
                                    Drop Ship Orders
                                 </h5>
                              </Col>
                           </Row>
                           <Row>
                              <Col>
                                 <WarehouseShippingScheduleCurrentWeekGrid
                                    data={data.dropShippingData}
                                    sortCallback={dropShippingScheduleSortChange}
                                    sortField={dropShippingScheduleSortField}
                                    sortDirection={dropShippingScheduleSortDirection}
                                    onUpdateRow={() => setLoaded(false)}
                                    exportXlsxCallback={() => exportXlsx(constants.SHIPPING_SCHEDULE_TYPES.DropShipping)}
                                    schedule={constants.SHIPPING_SCHEDULE_TYPES.DropShipping}
                                    scanCallback={handleScanOrder}
                                 />
                              </Col>
                           </Row>
                        </Row>
                     ) : null}

                     {/* PARTS SHIP SCHEDULE */}
                     {data && data.machinePartScheduleData?.length > 0 ? (
                        <Row className="mt-3 d-flex justify-content-center">
                           <Row>
                              <Col xs="8">
                                 <h5 style={{ textDecoration: "underline" }} >
                                    Machine Part Orders
                                 </h5>
                              </Col>
                           </Row>
                           <Row>
                              <Col>
                                 <WarehouseShippingScheduleCurrentWeekGrid
                                    data={data.machinePartScheduleData}
                                    sortCallback={onMachinePartScheduleSortChange}
                                    sortField={machinePartScheduleSortField}
                                    sortDirection={machinePartScheduleSortDirection}
                                    onUpdateRow={() => setLoaded(false)}
                                    exportXlsxCallback={() => exportXlsx(constants.SHIPPING_SCHEDULE_TYPES.MachinePartShipping)}
                                    schedule={constants.SHIPPING_SCHEDULE_TYPES.MachinePartShipping}
                                    scanCallback={handleScanOrder}
                                 />
                              </Col>
                           </Row>
                        </Row>
                     ) : null}

                     {/* CONSIGNED ORDER SHIP SCHEDULE */}
                     {data && data.consignedOrdersData?.length > 0 ? (
                        <Row className="mt-3 d-flex justify-content-center">
                           <Row>
                              <Col xs="8">
                                 <h5 style={{ textDecoration: "underline" }} >
                                    Consigned Orders
                                 </h5>
                              </Col>
                           </Row>
                           <Row>
                              <Col>
                                 <WarehouseShippingScheduleConsignedGrid
                                    data={data.consignedOrdersData}
                                    exportXlsxCallback={exportConsignedOrdersXlsx}
                                    scanCallback={handleScanOrder}
                                 />
                              </Col>
                           </Row>
                        </Row>
                     ) : null}

                     {/* SAMPLE ORDER SHIP SCHEDULE */}
                     {data && data.sampleOrderData?.length > 0 ? (
                        <Row className="mt-3 d-flex justify-content-center">
                           <Row>
                              <Col xs="8">
                                 <h5 style={{ textDecoration: "underline" }} >
                                    Sample Orders
                                 </h5>
                              </Col>
                           </Row>
                           <Row>
                              <Col>
                                 <WarehouseShippingScheduleSampleOrderGrid
                                    data={data.sampleOrderData}
                                    sortCallback={onSampleOrderScheduleSortChange}
                                    sortField={sampleOrderSortField}
                                    sortDirection={sampleOrderSortDirection}
                                    onUpdateRow={() => setLoaded(false)}
                                    exportXlsxCallback={exportSampleOrderXlsx}
                                 />
                              </Col>
                           </Row>
                        </Row>
                     ) : null}
                  </>
               ) : null}

               {/* Late Shipment Grids will only show on the today option or the late shipment option 
                  The lists containing the data for these will not be populated for anything else.
                  Late parts or DSHIP orders should show below all other grids that get populated. */}
               {data && data.lateSchedulePartsData?.length > 0 ? (
                  <Row className="mt-3 d-flex justify-content-center">
                     <Row>
                        <Col xs="8">
                           <h5 style={{ textDecoration: "underline" }} >
                              Late Machine Part Orders
                           </h5>
                        </Col>
                     </Row>
                     <Row>
                        <Col>
                           <WarehouseShippingScheduleLateShipGrid
                              data={data.lateSchedulePartsData}
                              onUpdateRow={() => setLoaded(false)}
                              exportXlsxCallback={() => exportLateXlsx(constants.SHIPPING_SCHEDULE_TYPES.MachinePartShipping)}
                              schedule={constants.SHIPPING_SCHEDULE_TYPES.MachinePartShipping}
                              scanCallback={handleScanOrder}
                           />
                        </Col>
                     </Row>
                  </Row>
               ) : null}

               {data && data.lateScheduleDropShipData?.length > 0 ? (
                  <Row className="mt-3 d-flex justify-content-center">
                     <Row>
                        <Col xs="8">
                           <h5 style={{ textDecoration: "underline" }} >
                              Late Drop Ship Orders
                           </h5>
                        </Col>
                     </Row>
                     <Row>
                        <Col>
                           <WarehouseShippingScheduleLateShipGrid
                              data={data.lateScheduleDropShipData}
                              onUpdateRow={() => setLoaded(false)}
                              exportXlsxCallback={() => exportLateXlsx(constants.SHIPPING_SCHEDULE_TYPES.DropShipping)}
                              schedule={constants.SHIPPING_SCHEDULE_TYPES.DropShipping}
                              scanCallback={handleScanOrder}
                           />
                        </Col>
                     </Row>
                  </Row>
               ) : null}

               {periodType === "lateRadio" &&
                  data?.lateScheduleData?.length <= 0 &&
                  data?.lateSchedulePartsData?.length <= 0 &&
                  data?.lateScheduleDropShipData?.length <= 0 ? (
                  <Row className="mt-3 mb-5 pb-5 d-flex justify-content-center">
                     <Row>
                        <Col xs="8">
                           <h5 style={{ color: 'orangered' }} >
                              There are no late shipments for the selected criteria
                           </h5>
                        </Col>
                     </Row>
                  </Row>
               ) : null}

               {/* Just some spacing for the bottom */}
               <Row className="mb-3 pb-3"></Row>
            </ServiceThreadSpinner>
         </Row>

         {/* Scan Order Modal */}
         <Modal isOpen={showModal}>
            <ModalHeader children="Scan Order" />
            <ModalBody>
               <Row className="mt-2">
                  <Col className="d-flex justify-content-center">
                     {showModal && barcodeString ? (
                        <ServiceThreadSpinner on={barcodeLoading} small>
                           {barcodeImageUrl ? <img alt="" src={barcodeImageUrl} /> : null}
                        </ServiceThreadSpinner>
                     ) : null}
                  </Col>
               </Row>
               <Row className="mt-2">
                  <Col className="d-flex justify-content-center">
                     <h6>{barcodeString}</h6>
                  </Col>
               </Row>
               <Row className="mt-2">
                  <Col>
                     <h3>Special Instructions:</h3>
                     <p>{specialInstructions}</p>
                  </Col>
               </Row>
               <Row className="mt-2">
                  <Col>
                     <h3>Shipping Notes:</h3>
                     <p>{shippingNotes}</p>
                  </Col>
               </Row>
            </ModalBody>
            <ModalFooter>
               <Button className="shredButton" onClick={() => setShowModal(false)}>
                  Close
               </Button>
            </ModalFooter>
         </Modal>
      </DesktopContainer>
   );
}
